import { useEffect, useState , useRef} from "react";
import {
  ArrowLeft,
  AlertCircle,
  Eye,
  EyeOff
} from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";

import { useAuth } from "../hooks/useAuth";
import { useTheme } from "next-themes";

interface SignUpPageProps {
  onNavigate: (page: string) => void;
}

export function SignUpPage({ onNavigate }: SignUpPageProps) {
  
const navigate = (url: string, page: string) => {
  window.history.pushState({}, "", url);
  onNavigate(page);
};


  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    experience: "",
    location: "",
    goals: "",
    userType: "swimmer" as "swimmer" | "coach"
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const errorTimeoutRef = useRef<number | null>(null);

  const { register: signUp, error, clearError } = useAuth();
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    return () => {
      clearError();
      setLocalError("");
      if (errorTimeoutRef.current) {
        window.clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, [clearError]);

  const showTransientError = (message: string, durationMs = 1500) => {
    setLocalError(message);
    if (errorTimeoutRef.current) {
      window.clearTimeout(errorTimeoutRef.current);
    }
    errorTimeoutRef.current = window.setTimeout(() => {
      setLocalError("");
      errorTimeoutRef.current = null;
    }, durationMs);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    clearError();
    setLocalError("");
    setIsSubmitting(true);
    
    try {

      if (!formData.firstName.trim()) {
        showTransientError("First name is required");
        return;
      }

      if (!formData.lastName.trim()) {
        showTransientError("Last name is required");
        return;
      }

      if (
          formData.password.trim().length < 6 ||
          formData.confirmPassword.trim().length < 6
        ) {
          showTransientError("Password must be at least 6 characters long");
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          showTransientError("Passwords do not match");
          return;
        }

        if (!agreedToTerms) {
          showTransientError("Please agree to the terms and conditions");
          return;
        }

        await signUp({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          type: formData.userType
        });

        window.history.pushState({}, "", "/signin");
        onNavigate("signin");
      } finally {
        setIsSubmitting(false);
      }
  };

  return (
  <div
    className="
      min-h-screen flex flex-col
      bg-gradient-to-br from-blue-50 to-indigo-100
      dark:from-slate-900 dark:to-slate-800
    "
  >
    {/* ================= FULL WIDTH HEADER ================= */}
    <header
      className="
        w-full border-b sticky top-0 z-50
        bg-white/80 dark:bg-slate-900/80
        backdrop-blur
        border-border
      "
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <button onClick={() => navigate("/", "landing")}>
          <img src={logoSrc} alt="Swimnerd Logo" className="h-10 w-auto" />
        </button>

        <nav className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/find-pool", "find-pool")}>
            Find Pool
          </Button>
          <Button variant="ghost" onClick={() => navigate("/blog", "blog")}>
            Blog
          </Button>
          <Button variant="ghost" onClick={() => navigate("/signin", "signin")}>
            Sign In
          </Button>
          <Button onClick={() => navigate("/signup", "signup")}>
            Get Started
          </Button>
      </nav>
      </div>
    </header>
    {/* ================= END HEADER ================= */}

    {/* ================= CENTERED CONTENT ================= */}
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Join Swimnerd
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Create your account to start your swimming journey
          </p>
        </div>

        <Card className="bg-white dark:bg-slate-900 border border-border">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">
              Create Account
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">

              {(localError || error) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {localError || error}
                  </AlertDescription>
                </Alert>
              )}

              {/* Account Type */}
              <div className="space-y-2">
                <Label>Account Type</Label>
                <div className="flex gap-6">
                  {["swimmer", "coach"].map(type => (
                    <label
                      key={type}
                      className="flex items-center gap-2 text-sm text-foreground"
                    >
                      <input
                        type="radio"
                        value={type}
                        checked={formData.userType === type}
                        onChange={e =>
                          handleInputChange("userType", e.target.value)
                        }
                      />
                      <span className="capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={e => handleInputChange("firstName", e.target.value)}
                  required
                />
                <Input
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={e => handleInputChange("lastName", e.target.value)}
                  required
                />
              </div>

              {/* Email */}
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={e => handleInputChange("email", e.target.value)}
                required
              />

              {/* Password */}
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={e => handleInputChange("password", e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={e => handleInputChange("confirmPassword", e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(v => !v)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>

              {/* Terms */}
              <div className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={e => setAgreedToTerms(e.target.checked)}
                />
                I agree to the Terms & Privacy Policy
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                Create Account
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Already have an account?
                <Button
                  variant="link"
                  className="p-0 ml-1 h-auto text-sm"
                  onClick={() => navigate("/signin", "signin")}
                >
                  Sign in
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/", "landing")}
            className="flex items-center gap-2 mx-auto text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </div>

      </div>
    </main>
  </div>
);
}
